import {
  Button,
  Card,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Box, Typography } from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import { AddTicketsData, Order, TicketsData } from "../../types/order";
import { getDate } from "../../helpers/dateHelper";
import { TextField } from "@material-ui/core";
import { TablePagination } from "@material-ui/core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import RemoveItem from "../common/Remove/RemoveItem";
import DoneOutlineIcon from "@material-ui/icons/DoneOutline";
import LastPageIcon from "@material-ui/icons/LastPage";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import "./index.css";

const getCompetitionName = (name: string | undefined): string => {
  switch (name) {
    case "Dream home":
      return "raffle";
    case "Lifestyle":
      return "prize";
    case "Fixed Odds":
      return "fixedOdds";
    default:
      return "raffle";
  }
};
interface Props {
  order: Order | undefined;
  userId: string;
  isShow: boolean;
}

interface AddTicketsProps {
  closeModal: () => void;
  handleTicketsCount: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ticketsCount: number;
  saveTickets: () => void;
}

const AddTickets: FC<AddTicketsProps> = ({
  closeModal,
  handleTicketsCount,
  ticketsCount,
  saveTickets,
}) => {
  return (
    <>
      <Box padding="2em">
        <Box display="flex" justifyContent="center" padding="1em">
          <Typography variant="h6" className="base-color">
            Add tickets
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-evenly"
          className="add-tickets-exactly"
          alignItems="center"
          paddingLeft="5em"
        >
          <TextField
            type="number"
            label=""
            className="add-tickets-field"
            value={ticketsCount}
            inputProps={{ step: 1 }}
            onChange={handleTicketsCount}
            defaultValue={1}
            required
          />
          <Typography className="base-color">Tickets</Typography>
        </Box>
        <Box display="flex" justifyContent="space-evenly" paddingTop="1em">
          <Button
            className="user-cancel-button user-modal-buttons"
            disabled={ticketsCount ? false : true}
            onClick={saveTickets}
          >
            Save
          </Button>
          <Button
            className="user-cancel-button user-modal-buttons"
            onClick={closeModal}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};

const ViewTickets: FC<Props> = ({ order, userId, isShow }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [state, setState] = useState({
    tickets: Array<TicketsData>(),
    total: 0,
    page: 1,
    perPage: 100,
    field: "_id",
    order: "asc",
    isStatusActive: false,
  });

  const [modal, setModal] = useState({
    addTickets: false,
    removeTicket: false,
    ticketRemoveCode: "",
  });

  const [selectedValue, setSelectedValue] = useState({
    ticketsCount: 1,
  });

  useEffect(() => {
    getTickets(order, 1, 100);
  }, []);

  const getTickets = async (
    orderIds: Order | undefined,
    page: number,
    perPage: number
  ) => {
    const result = (await dataProvider.getList("orders", {
      filter: {
        ids: orderIds?.ordersIds,
        collection: getCompetitionName(orderIds?.competition),
        id: order?.competitionId,
        page: page,
        perPage: perPage,
      },
      pagination: { page: page, perPage: perPage },
      sort: { field: "id", order: "ASC" },
    })) as any;
    if (result.data.length > 0) {
      const isActive = result.data[0].isActive;
      setState({
        ...state,
        tickets: result.data,
        isStatusActive: isActive,
        total: result.total,
        page: page,
        perPage: perPage,
      });
    }
  };

  const openAddTicketsModal = () => {
    setModal({ ...modal, addTickets: true });
  };

  const openRemoveTicketModal = (code: string) => {
    setModal({ ...modal, removeTicket: true, ticketRemoveCode: code });
  };

  const closeAddTicketsModal = () => {
    setModal({ ...modal, addTickets: false });
  };

  const closeRemoveTicketModal = () => {
    setModal({ ...modal, removeTicket: false });
  };

  const saveTickets = async () => {
    const data: AddTicketsData = {
      ticketsCount: selectedValue.ticketsCount,
      competition: getCompetitionName(order?.competition),
      prizeId: order?.prizeId || "",
    };
    try {
      const result = await dataProvider.addTickets(userId, data);
      if (result.data) {
        notify("Tickets added", "info");
        closeAddTicketsModal();
        refresh();
      }
    } catch (error) {
      notify(error.data.error, "error");
    }
  };

  const handleTicketsCount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(e.currentTarget.value);
    if (count < 1 || count > 5000) {
      return;
    }
    setSelectedValue({ ...selectedValue, ticketsCount: count });
  };

  const removeTicket = async () => {
    const result = await dataProvider.deleteMany("orders/ticket", {
      ids: order?.ordersIds,
      code: modal.ticketRemoveCode,
      isActive: state.isStatusActive,
    });
    if (result.data) {
      notify("Ticket has been deleted", "info");
      refresh();
    }
  };

  const handleFirstPage = () => {
    getTickets(order, 1, state.perPage);
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getTickets(order, page, state.perPage);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getTickets(order, newPage + 1, state.perPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const perPage = parseInt(event.target.value, 10);
    getTickets(order, state.page, perPage);
  };

  return (
    <>
      <Box
        display="flex"
        fontWeight="600"
        padding="1em"
        justifyContent="center"
      >
        <Typography variant="h6" className="base-color">
          <Box fontWeight="600">View Tickets</Box>
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        className="base-color header-font"
      >
        <Box>
          <Box fontWeight="600" className="base-color">
            Competition
          </Box>
          <Box className="base-color">
            {order ? order.competition : "No data"}
          </Box>
          <Box fontWeight="600" className="base-color">
            Start Date
          </Box>
          <Box className="base-color">
            {order
              ? order.startCompetitionDate
                ? order.startCompetitionDate
                : "No data"
              : "No data"}
          </Box>
        </Box>
        <Box>
          <Box fontWeight="600" className="base-color">
            Product
          </Box>
          <Box className="base-color">{order ? order.name : "No data"}</Box>
          <Box fontWeight="600" className="base-color">
            Finish date
          </Box>
          <Box className="base-color">
            {order ? getDate(new Date(order.drawDate)) : "No data"}
          </Box>
        </Box>
        <Box>
          <Box fontWeight="600" className="base-color">
            Number of tickets
          </Box>
          <Box className="base-color">{order ? state.total : "No data"}</Box>
        </Box>
        {state.isStatusActive && !isShow ? (
          <Box display="flex" alignItems="flex-end">
            <Button
              className="user-cancel-button user-modal-buttons"
              onClick={openAddTicketsModal}
            >
              Add Tickets
            </Button>
          </Box>
        ) : (
          <div></div>
        )}
      </Box>

      <Box marginTop="1em">
        <Card>
          <TableContainer className="userTicketsTableList">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id="active-table-head">Ticket ID</TableCell>
                  <TableCell id="active-table-head">Purchase date</TableCell>
                  <TableCell id="active-table-head">Status</TableCell>
                  <TableCell id="active-table-head">Price</TableCell>
                  <TableCell id="active-table-head-actions">Discount</TableCell>
                  {state.isStatusActive && !isShow && (
                    <TableCell id="active-table-head-actions">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.tickets.map((item) => {
                  return (
                    <TableRow>
                      <TableCell>{item.code}</TableCell>
                      <TableCell>
                        {getDate(new Date(item.purchaseDate))}
                      </TableCell>
                      <TableCell>
                        {item.status || item.price === 0 ? "Not paid" : "Paid"}
                      </TableCell>
                      <TableCell>
                        {item.price > 0 ? item.price.toFixed(2) : "-"}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" justifyContent="center">
                          {item.discount ? <DoneOutlineIcon /> : null}
                        </Box>
                      </TableCell>
                      {state.isStatusActive && !isShow && (
                        <TableCell>
                          <div
                            className="actions-table-body"
                            onClick={() => openRemoveTicketModal(item.code)}
                          >
                            <DeleteForeverIcon className="delete-icon" />
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display="flex"
            alignItems="center"
            position="relative"
            paddingRight="1em"
            justifyContent="flex-end"
          >
            <TablePagination
              component="div"
              count={state.total}
              page={state.page - 1}
              onPageChange={handleChangePage}
              rowsPerPage={state.perPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className="pagination-table"
            />
            <div
              className="last-page-icon"
              title="Last Page"
              onClick={handleLastPage}
            >
              <LastPageIcon className="last-icon" />
            </div>
            <Box position="absolute" right="160px">
              <div
                className="last-page-icon"
                title="First Page"
                onClick={handleFirstPage}
              >
                <FirstPageIcon />
              </div>
            </Box>
          </Box>

          <Dialog
            fullWidth
            open={modal.addTickets}
            onClose={closeAddTicketsModal}
            id="add-tickets-dialog"
          >
            <DialogContent>
              <AddTickets
                closeModal={closeAddTicketsModal}
                handleTicketsCount={handleTicketsCount}
                ticketsCount={selectedValue.ticketsCount}
                saveTickets={saveTickets}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            fullWidth
            open={modal.removeTicket}
            onClose={closeRemoveTicketModal}
            // id="add-tickets-dialog"
          >
            <DialogContent>
              <RemoveItem
                closeModal={closeRemoveTicketModal}
                removeUser={removeTicket}
                mainText="ticket"
                minorText=""
              />
            </DialogContent>
          </Dialog>
        </Card>
      </Box>
    </>
  );
};

export default ViewTickets;
